import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Search, Filter, Download, Eye, CheckCircle, XCircle, Clock,
  ChevronDown, User, Calendar, Euro, Archive
} from 'lucide-react';
import { inscriptionsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const AdminInscriptions = () => {
  const [inscriptions, setInscriptions] = useState([]);
  const [filteredInscriptions, setFilteredInscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const { toast } = useToast();

  useEffect(() => {
    loadInscriptions();
  }, []);

  useEffect(() => {
    filterInscriptions();
  }, [inscriptions, search, statusFilter]);

  const loadInscriptions = async () => {
    try {
      const res = await inscriptionsAPI.list();
      setInscriptions(res.data);
    } catch (error) {
      console.error('Error loading inscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadZip = async (inscription) => {
    try {
      toast({ title: 'Préparation du ZIP', description: `Inscription ${inscription.numero || ''}…` });
      await inscriptionsAPI.downloadZip(inscription.id, inscription.numero);
    } catch (e) {
      toast({
        title: 'Téléchargement impossible',
        description: e?.response?.status === 403 ? 'Accès réservé aux administrateurs.' : 'Erreur lors de la génération du ZIP.',
        variant: 'destructive',
      });
    }
  };

  const filterInscriptions = () => {
    let filtered = [...inscriptions];
    
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(i => 
        i.numero?.toLowerCase().includes(searchLower) ||
        i.enfants?.some(e => 
          e.nom?.toLowerCase().includes(searchLower) ||
          e.prenom?.toLowerCase().includes(searchLower)
        )
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(i => i.status === statusFilter);
    }
    
    setFilteredInscriptions(filtered);
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await inscriptionsAPI.updateStatus(id, newStatus);
      toast({ title: 'Statut mis à jour', description: `Inscription ${newStatus}` });
      loadInscriptions();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de mettre à jour le statut', variant: 'destructive' });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock },
      validated: { bg: 'bg-blue-100', text: 'text-blue-800', icon: CheckCircle },
      paid: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle },
    };
    const labels = { pending: 'En attente', validated: 'Validée', paid: 'Payée', cancelled: 'Annulée' };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon className="h-3 w-3 mr-1" />
        {labels[status] || status}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-inscriptions">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <h1 className="text-2xl font-bold text-[#2C3C65]">Inscriptions</h1>
          <div className="flex flex-wrap gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 w-64"
                data-testid="search-inscriptions"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded-lg px-4 py-2"
              data-testid="filter-status"
            >
              <option value="all">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="validated">Validées</option>
              <option value="paid">Payées</option>
              <option value="cancelled">Annulées</option>
            </select>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total', value: inscriptions.length, color: 'bg-gray-100' },
            { label: 'En attente', value: inscriptions.filter(i => i.status === 'pending').length, color: 'bg-yellow-100' },
            { label: 'Validées', value: inscriptions.filter(i => i.status === 'validated').length, color: 'bg-blue-100' },
            { label: 'Payées', value: inscriptions.filter(i => i.status === 'paid').length, color: 'bg-green-100' },
          ].map((stat, idx) => (
            <div key={idx} className={`${stat.color} rounded-lg p-4`}>
              <p className="text-sm text-gray-600">{stat.label}</p>
              <p className="text-2xl font-bold text-[#2C3C65]">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#285BBB] mx-auto"></div>
            </div>
          ) : filteredInscriptions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#6777A1]">Aucune inscription trouvée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">N°</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Enfant(s)</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Parent</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredInscriptions.map((inscription) => (
                    <motion.tr
                      key={inscription.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4">
                        <Link
                          to={`/admin/inscriptions/${inscription.id}`}
                          className="font-medium text-[#285BBB] hover:underline"
                          data-testid={`open-inscription-${inscription.id}`}
                        >
                          {inscription.numero}
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <User className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm">
                            {inscription.enfants?.map(e => `${e.prenom} ${e.nom}`).join(', ')}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {inscription.pere?.nom || inscription.mere?.nom || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar className="h-4 w-4 mr-1" />
                          {new Date(inscription.created_at).toLocaleDateString('fr-FR')}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center font-medium">
                          <Euro className="h-4 w-4 mr-1 text-gray-400" />
                          {inscription.total}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(inscription.status)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/admin/inscriptions/${inscription.id}`}
                            className="p-2 text-gray-400 hover:text-[#285BBB]"
                            title="Voir le détail"
                            data-testid={`view-inscription-${inscription.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                          <a
                            href={inscriptionsAPI.getPDF(inscription.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-400 hover:text-[#285BBB]"
                            title="Télécharger PDF récapitulatif"
                            data-testid={`download-pdf-${inscription.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </a>
                          <button
                            type="button"
                            onClick={() => handleDownloadZip(inscription)}
                            className="p-2 text-gray-400 hover:text-emerald-600"
                            title="Télécharger ZIP (PDF + pièces jointes)"
                            data-testid={`download-zip-${inscription.id}`}
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                          <div className="relative group">
                            <button className="p-2 text-gray-400 hover:text-gray-600">
                              <ChevronDown className="h-4 w-4" />
                            </button>
                            <div className="absolute right-0 top-full hidden group-hover:block bg-white shadow-lg rounded-lg py-2 min-w-[150px] z-10">
                              <button
                                onClick={() => updateStatus(inscription.id, 'validated')}
                                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                              >
                                Valider
                              </button>
                              <button
                                onClick={() => updateStatus(inscription.id, 'paid')}
                                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                              >
                                Marquer payée
                              </button>
                              <button
                                onClick={() => updateStatus(inscription.id, 'cancelled')}
                                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                              >
                                Annuler
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminInscriptions;
